/**
 * What needs the team (section 6), in plain words, each leading to where it is fixed: problems that
 * stop the next publish (those a setup step clears are that step's while the guide shows), failed
 * uploads, videos waiting for the encoder over an hour, items leaving the app within 7 days, and
 * storage over 80 %.
 */

import type { Problem } from "@/api/admin";
import { href, itemRoute, type Route } from "@/lib/route";
import { plainProblem } from "@/publishing/problems";
import { isInApp } from "@/publishing/status";
import type { Pack } from "@/vocabulary/vocabulary";
import type { HomeData } from "./use-home-data";

export interface Attention { tone: "critical" | "warning"; icon: string; title: string; detail: string; href: string }

const HOUR = 3_600_000;
const DAY = 24 * HOUR;

export function attentionList({ brand, pack, data, errors, nameOf, now = Date.now() }: {
  brand: string;
  pack: Pack;
  data: HomeData;
  /** The publish problems Needs attention names (not those a setup step shows). */
  errors: Problem[];
  nameOf: (id: string) => string | undefined;
  now?: number;
}): Attention[] {
  const link = (r: Omit<Route, "brand">) => href({ brand, ...r } as Route);
  const attention: Attention[] = [];
  if (errors.length === 1) attention.push({ tone: "critical", icon: "close-circle", title: plainProblem(errors[0]!.message, pack, nameOf), detail: "This stops the next publish.", href: link({ section: "review" }) });
  else if (errors.length > 1) attention.push({ tone: "critical", icon: "close-circle", title: `${errors.length} problems stop the next publish`, detail: errors.slice(0, 2).map((p) => plainProblem(p.message, pack, nameOf)).join(" · ") + (errors.length > 2 ? " …" : ""), href: link({ section: "review" }) });
  const failed = data.assets.filter((a) => a.status === "failed");
  if (failed.length) attention.push({ tone: "critical", icon: "close-circle", title: `${failed.length} ${failed.length === 1 ? "upload" : "uploads"} failed`, detail: failed[0]?.error ? `“${failed[0].title}”: ${failed[0].error}` : "Upload the file again, or remove it.", href: link({ section: "settings", page: "files" }) });
  const waiting = data.assets.filter((a) => a.kind === "video" && a.status === "uploading" && now - a.updatedAt > HOUR);
  if (waiting.length) attention.push({ tone: "warning", icon: "hourglass", title: `${waiting.length} ${waiting.length === 1 ? "video has" : "videos have"} waited over an hour for the encoder`, detail: "The studio's Mac encodes videos. Check that it is on and the media agent is running.", href: link({ section: "settings", page: "files" }) });
  const ending = data.full.filter((e) => {
    const until = Date.parse(String((e.data["availability"] as { until?: string } | undefined)?.until ?? ""));
    return isInApp(e.publishState) && !Number.isNaN(until) && until > now && until - now < 7 * DAY;
  });
  if (ending.length) attention.push({ tone: "warning", icon: "clock", title: `${ending.length} ${ending.length === 1 ? "item leaves" : "items leave"} the app within 7 days`, detail: ending.slice(0, 3).map((e) => e.label).join(", ") + (ending.length > 3 ? "…" : ""), href: link(itemRoute(brand, ending[0]!.type, ending[0]!.id)) });
  const used = storageShare(data);
  if (used !== null && used > 0.8) attention.push({ tone: used >= 0.95 ? "critical" : "warning", icon: "warning", title: `Storage is ${Math.round(used * 100)}% full`, detail: "Remove files you no longer use, or ask for more storage.", href: link({ section: "settings", page: "files" }) });
  return attention;
}

/** Storage used (stored and reserved) as a share of the budget; null when the budget is unknown. */
export function storageShare(data: HomeData): number | null {
  return data.budget && data.budget.budgetBytes > 0 ? (data.budget.storedBytes + data.budget.reservedBytes) / data.budget.budgetBytes : null;
}
